import axios from 'axios';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { emptyObject } from 'ui-components/default-props/empty';

import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import { useEntitiesFulfillmentPlan } from '../../../../hooks/entities/selectors/fulfillment-plans';
import { bulkloadFulfillmentPlans } from '../../../../store/bulkload/bulkload.actions';
import { useEnableReship } from '../../../../feature-flags/hooks/use-enable-reship';
import { ADD_EDIT_SHIPMENT_ITEMS_ACTIONS, useTrackAddEditItemsShipmentAction } from '../../hooks/use-track-add-edit-items-action';
import { useEnableAddShipmentItems } from '../../../../feature-flags/hooks/use-enable-add-shipment-items';

const getModifiedItems = (fulfillmentPlanItems, proposedItems) => {
  const fulfillmentPlanItemMap = fulfillmentPlanItems.reduce(
    (acc, item) => ({
      ...acc,
      [item.fulfillmentPlanItemId]: item,
    }),
    {},
  );

  return proposedItems.filter((item) => {
    const matchedItem = fulfillmentPlanItemMap[item.fulfillmentPlanItemId];
    if (!matchedItem) {
      return false;
    }

    return (
      matchedItem.sku !== item.sku ||
      matchedItem.name !== item.name ||
      matchedItem.productId !== item.productId ||
      matchedItem.quantity !== item.quantity ||
      matchedItem.unitPrice?.value !== item.unitPrice?.value
    );
  });
};

const buildSavePayload = (items) => {
  return items.reduce(
    (acc, item) => {
      const addItemToAcc = (category, currentItem) => {
        return {
          ...acc,
          [category]: [...acc[category], currentItem],
        };
      };

      if (item.isAdded) {
        return addItemToAcc('itemsToAdd', item);
      }
      if (item.isDeleted) {
        return addItemToAcc('itemsToDeactivate', item);
      }

      return addItemToAcc('itemsToModify', item);
    },
    {
      itemsToAdd: [],
      itemsToModify: [],
      itemsToDeactivate: [],
    },
  );
};

const useSaveFulfillmentPlanItems = (fulfillmentPlanId, onSuccess, onError) => {
  const dispatch = useDispatch();
  const fulfillmentPlan = useEntitiesFulfillmentPlan(fulfillmentPlanId) || emptyObject;
  const hasAdditionalItems = fulfillmentPlan.items?.some((item) => !item.salesOrderItemId) || false;
  const [isSaving, setIsSaving] = useAsyncSafeState(false);
  const isReshipEnabled = useEnableReship();
  const isAddShipmentItemsEnabled = useEnableAddShipmentItems();

  const trackAddEditItemsShipmentSuccess = useTrackAddEditItemsShipmentAction(ADD_EDIT_SHIPMENT_ITEMS_ACTIONS.SUCCESS);
  const trackAddEditItemsShipmentError = useTrackAddEditItemsShipmentAction(ADD_EDIT_SHIPMENT_ITEMS_ACTIONS.ERROR);

  const save = useCallback(
    async (items) => {
      const { itemsToAdd, itemsToModify: proposedItemsToModify, itemsToDeactivate } = buildSavePayload(items);

      const itemsToModify = getModifiedItems(fulfillmentPlan.items, proposedItemsToModify);
      const changesMade = itemsToAdd.length || itemsToModify.length || itemsToDeactivate.length;

      if (!changesMade) {
        onSuccess({});
        trackAddEditItemsShipmentSuccess({
          fulfillmentPlanId,
          itemsToAdd,
          itemsToModify,
          itemsToDeactivate,
          hasAdditionalItems,
        });
        return;
      }

      setIsSaving(true);
      try {
        await axios.post('/fulfillmentplan/items/edit', {
          itemsToAdd,
          itemsToModify,
          itemsToDeactivate,
          allowForMarketplaceOrders: isReshipEnabled || isAddShipmentItemsEnabled,
        });
        dispatch(bulkloadFulfillmentPlans([fulfillmentPlanId]));
        onSuccess();
        trackAddEditItemsShipmentSuccess({
          fulfillmentPlanId,
          itemsToAdd,
          itemsToModify,
          itemsToDeactivate,
          hasAdditionalItems,
        });
      } catch (e) {
        const errors = e?.response?.data?.Errors || [e];
        onError(errors);
        trackAddEditItemsShipmentError({
          errors,
          fulfillmentPlanId,
          itemsToAdd,
          itemsToModify,
          itemsToDeactivate,
          hasAdditionalItems,
        });
      } finally {
        setIsSaving(false);
      }
    },
    [
      fulfillmentPlan.items,
      setIsSaving,
      onSuccess,
      dispatch,
      fulfillmentPlanId,
      trackAddEditItemsShipmentSuccess,
      onError,
      trackAddEditItemsShipmentError,
      hasAdditionalItems,
      isReshipEnabled,
      isAddShipmentItemsEnabled,
    ],
  );

  return {
    isSaving,
    save,
  };
};
export default useSaveFulfillmentPlanItems;
